import type { ThemeId } from '../../theme/ThemeContext'
import { AsyncPullLoadingIndicator } from '../async/AsyncPullLoadingIndicator'
import { PageAsyncEmptyState } from '../async/PageAsyncEmptyState'
import { PageAsyncErrorState } from '../async/PageAsyncErrorState'
import { ChromePrimaryButton } from '../ChromeActions'

export type ComicStateKind = 'loading' | 'empty' | 'error'

function EmptyBookshelfIllustration({ angular }: { angular: boolean }) {
  const rx = angular ? 0 : 6
  const sw = angular ? 1.9 : 1.7
  return (
    <svg
      className="mx-auto h-[5rem] w-[5rem] shrink-0 text-ink-soft/55"
      viewBox="0 0 72 72"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M12 14h14v44H14a2 2 0 0 1-2-2V16a2 2 0 0 1 2-2h0Z"
        className="stroke-current"
        strokeWidth={sw}
        strokeOpacity={0.55}
      />
      <path
        d="M26 12h14v46H28a2 2 0 0 1-2-2V14a2 2 0 0 1 2-2h0Z"
        className="stroke-current"
        strokeWidth={sw}
        strokeOpacity={0.75}
      />
      <path
        d="M40 15h14v43H42a2 2 0 0 1-2-2V17a2 2 0 0 1 2-2h0Z"
        className="stroke-current"
        strokeWidth={sw}
        strokeOpacity={0.55}
      />
      <rect
        x="18"
        y="22"
        width="8"
        height="2"
        rx={rx / 2}
        className="fill-current"
        fillOpacity={0.35}
      />
      <rect
        x="32"
        y="20"
        width="8"
        height="2"
        rx={rx / 2}
        className="fill-current"
        fillOpacity={0.45}
      />
    </svg>
  )
}

type Props = {
  theme: ThemeId
  kind: ComicStateKind
  /** kind=loading 时顶栏文案 */
  loadingLabel?: string
  detail?: string
  onRetry?: () => void
  onCreate?: () => void
}

/** 漫画书架：加载 / 空 / 错误占位（加载用 {@link AsyncPullLoadingIndicator}） */
export function ThemedComicState({ theme, kind, loadingLabel, detail, onRetry, onCreate }: Props) {
  const angular = theme === 'pixel' || theme === 'geek'
  const terminalish = theme === 'geek'

  if (kind === 'loading') {
    return (
      <AsyncPullLoadingIndicator
        angular={angular}
        label={loadingLabel ?? (terminalish ? 'loading_shelf…' : '正在加载书架…')}
        className="pb-4"
      />
    )
  }

  if (kind === 'error') {
    return (
      <PageAsyncErrorState
        theme={theme}
        title={terminalish ? 'shelf_fetch_failed' : '作品列表加载失败'}
        detail={detail}
        onRetry={onRetry}
        illustration={<EmptyBookshelfIllustration angular={angular} />}
      />
    )
  }

  return (
    <PageAsyncEmptyState
      illustration={<EmptyBookshelfIllustration angular={angular} />}
      title="书架还是空的"
      description="灵思尽绘千篇卷，悉数珍藏方寸间"
      action={
        onCreate ? (
          <ChromePrimaryButton density="compact" onClick={onCreate}>
            新建漫画
          </ChromePrimaryButton>
        ) : undefined
      }
    />
  )
}
